import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Api, {Utils} from '../Api';
import NoProducts from '../components/NoProducts';
import Processing from '../components/Processing';
import LightBox from '../components/LightBox';
import CategoryBar from '../components/CategoryBar';
import BreadCrumb from '../components/BreadCrumb';

import RetailerProduct from './ProductModal';
import RecommendationGrid from './RecommendationGrid';

import {
  CATEGORY_FILTERS,
  CATEGORY_FILTERS_VALUE_TO_NAME,
  SHOP_ALL_CATEGORY,
  JUST_FOR_YOU_CATEGORY
} from '../consts';
import {capitalizeEveryWord} from '../lib/StringUtils';
import {generateUUID} from '../lib/UUID';

import './Retailer.less';

const RECOMMENDATION_TYPES = {
  SEARCH: 'search',
  CATEGORY: 'category'
};
const OFFSET_TO_LOAD = 300;

const calculateButtonText = selections => {
  let totalCases = 0;
  selections.forEach(selection => {
    totalCases += selection.batches;
  });

  if (totalCases >= 2) return 'Add Cases to Bag';

  return 'Add Case to Bag';
};

export default class Retailer extends Component {
  static contextTypes = {
    router: PropTypes.object
  };

  constructor(props) {
    super(props);
    Utils.bindThis(
      this,
      [
        'selectProduct',
        'selectCollection',
        'getMargin',
        'setCategory',
        'categoryBar',
        'closeProductModal',
        'productModal',
        'redirectToRetailerPage',
        'addToCart',
        'getRecommendedProduct',
        'getCartSelection',
        'onScroll',
        'onUserChanged'
      ]
    );

    let productToken = props.params.productId ? props.params.productId : null;

    let activeCategory = JUST_FOR_YOU_CATEGORY;
    let categorySearching = false;
    if (props.params.categoryName) {
      activeCategory = props.params.categoryName.replace(/\+/g, ' ');
      categorySearching = true;
    } else if (props.location.query.category) {
      activeCategory = props.location.query.category;
    }

    let activeSubcategory = null;
    if (activeCategory && props.location.query.subcategory) {
      activeSubcategory = props.location.query.subcategory;
    }

    let activeFilter = null;
    let filterValue = props.location.query.filter;
    if (filterValue) {
      let filterName = CATEGORY_FILTERS_VALUE_TO_NAME[filterValue];
      activeFilter = {name: filterName, value: filterValue};
    }

    this.categoryRequestLock = null;
    this.state = {
      error: null,
      retailer: null,
      user: Api.cache.getUser(),
      category: activeCategory,
      subcategory: activeSubcategory,
      filter: activeFilter,
      activeRecommendationType: RECOMMENDATION_TYPES.CATEGORY,
      categorySearching: categorySearching,
      categories: Api.cache.getCategories(),
      categoryRecommendations: null,
      margins: {},
      isSearching: !!Api.cache.getProductSearchTerm(),
      productToken: productToken,
      searchVisible: Api.cache.getProductSearchActive(),
      searchTerm: Api.cache.getProductSearchTerm(),
      searchRecommendations: null,
      cartVisible: Api.cache.getCartVisible(),
      currentFilter: Api.cache.getCurrentRecommendationFilter(),
      recommendations: Api.cache.getCurrentRecommendationSet()
    };
  }

  selectProduct(product) {
    let url = `/retailer/${this.props.params.retailerToken}/product/${product.token}`;
    this.context.router.push(url);
  }

  selectCollection(collection) {
    let url = `/retailer/${this.props.params.retailerToken}/collection/${collection.token}`;
    this.context.router.push(url);
  }

  componentWillMount() {
    Api.startListen(this, 'categories', Api.cache.eventTypes.CATEGORIES_CHANGED);
    Api.startListen(this, 'searchVisible', Api.cache.eventTypes.PRODUCT_SEARCH_ACTIVE_CHANGED);
    Api.startListen(this, 'searchTerm', Api.cache.eventTypes.PRODUCT_SEARCH_TERM_CHANGED);
    Api.startListen(this, 'cartVisible', Api.cache.eventTypes.CART_VISIBLE_CHANGED);
    Api.startListen(this, 'currentFilter', Api.cache.eventTypes.CURRENT_RECOMMENDATION_FILTER_CHANGED);
    Api.startListen(this, 'recommendations', Api.cache.eventTypes.CURRENT_RECOMMENDATION_SET_CHANGED);
    Api.cache.addChangeListener(this.onUserChanged, Api.cache.eventTypes.USER_CHANGED);
  }

  componentDidMount() {
    let loaded = this.loadRetailer(this.props.params.retailerToken);
    if (loaded) {
      loaded.then(() => {
        if (this.state.category || this.state.filter || this.state.searchTerm) {
          this.updateRecommendations();
        }
      });
    }
    window.scrollTo(0, 0);
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    Api.stopListen(this, Api.cache.eventTypes.CATEGORIES_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.PRODUCT_SEARCH_ACTIVE_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.PRODUCT_SEARCH_TERM_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.RECOMMENDATIONS_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.CART_VISIBLE_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.CURRENT_RECOMMENDATION_FILTER_CHANGED);
    Api.stopListen(this, Api.cache.eventTypes.CURRENT_RECOMMENDATION_SET_CHANGED);
    Api.cache.removeChangeListener(this.onUserChanged, Api.cache.eventTypes.USER_CHANGED);
    window.removeEventListener('scroll', this.onScroll);
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.productToken !== nextProps.params.productId) {
      this.setState({
        productToken: nextProps.params.productId
      });
    }
  }

  componentDidUpdate(oldProps, oldState) {
    if (this.props.params.retailerToken !== oldProps.params.retailerToken) {
      // If our retailer ever changes, load the new one.
      let loaded = this.loadRetailer(this.props.params.retailerToken);
      if (loaded) {
        loaded.then(() => {
          if (this.state.category || this.state.filter || this.state.searchTerm) {
            this.updateRecommendations();
          }
        });
        return;
      }
    }

    let shouldUpdate = false;
    // Simple search term changed. Search bar is visible
    shouldUpdate = shouldUpdate
      || (oldState.searchTerm !== this.state.searchTerm && this.state.searchVisible);

    // Search bar is no longer visible
    shouldUpdate = shouldUpdate
      || (!this.state.searchVisible && oldState.searchVisible);

    // Search bar is now visible and it already has a searchTerm
    shouldUpdate = shouldUpdate
      || (this.state.searchVisible && !oldState.searchVisible && this.state.searchTerm.trim().length > 0);

    // Category/Subcategory/Filter is changed
    shouldUpdate = shouldUpdate || (oldState.category !== this.state.category
      || oldState.subcategory !== this.state.subcategory
      || (oldState.filter && oldState.filter.value) !== (this.state.filter && this.state.filter.value));

    if (shouldUpdate) this.updateRecommendations();

    if (this.state.productToken) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }
  }

  onScroll() {
    // Only check to load more if category recommendations is active
    // and there is more to load
    if (this.state.activeRecommendationType !== RECOMMENDATION_TYPES.CATEGORY
      || !this.state.categoryRecommendations
      || !this.state.categoryRecommendations.loadMore) {
      return;
    }

    const document = window.document;
    const documentHeight = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    );

    const windowPos = window.pageYOffset || document.documentElement.scrollTop;
    if (windowPos + window.innerHeight > (documentHeight - OFFSET_TO_LOAD)) {
      this.state.categoryRecommendations.loadMore();
    }
  }

  onUserChanged() {
    let newUser = Api.cache.getUser();
    if (newUser.token !== (this.state.user && this.state.user.token)) {
      Api.cache.setProductSearchRecommendations(null);
      Api.cache.setCategories(null);
      this.setState({
        user: newUser
      });
    }
  }

  getRecommendedProduct(productToken) {
    let recommendedProduct = null;

    if (this.state.recommendations && this.state.recommendations.products) {
      recommendedProduct = this.state.recommendations.products[productToken];
    }

    if (recommendedProduct) {
      return Promise.resolve(recommendedProduct);
    }

    return Api.getProduct(productToken, true)
      .then(productResponse => productResponse.products[productToken]);
  }

  loadRetailer(retailerToken) {
    return Api.getRetailer(retailerToken, true)
      .then(
        retailer => new Promise((accept, reject) => {
            this.setState({ retailer }, accept);
        })
      )
      .then(() => Api.getUser())
      .then(user => {
        let isEmployee = user && user.is_employee;
        let isInvited = this.state.retailer && this.state.retailer.invited;

        if (isInvited || isEmployee) return Api.getCategories();
      })
      .catch(
        (error) => {
          if (error.httpStatusCode === 401) {
            let user = Api.cache.getUser();
            if (user && user.retailer_token && user.retailer_token !== retailerToken) {
              // Send the user to the Retailer they actually own.
              this.context.router.push(`/retailer/${user.retailer_token}`);
            } else if (!user) {
              // Handle user not loaded
              return Api.getUser().then(
                (user) => {
                  if (!user) {
                    this.context.router.push('/user');
                    return;
                  }

                  if (!user.retailer_token) {
                    this.context.router.push('/');
                    return;
                  }

                  this.context.router.push(`/retailer/${user.retailer_token}`);
                },
                () => {
                  this.context.router.push('/user');
                }
              )
            } else {
              // Should never happen
              this.context.router.push('/');
            }
          } else {
            console.error(error);
            this.setState({error});
          }
        }
      );
  }

  updateRecommendations() {
    if (this.state.searchVisible && this.state.searchTerm && this.state.searchTerm.trim().length > 0) {
      this.loadSearchProducts();
    } else {
      this.loadCategoryProducts();
    }
  }

  loadSearchProducts() {
    Api.cache.setCurrentRecommendationSet(null);
    this.setState({
      activeRecommendationType: RECOMMENDATION_TYPES.SEARCH,
      isSearching: true,
      categorySearching: false,
      searchRecommendations: null
    }, () => {
      Api.productBrowseNew({ query: this.state.searchTerm })
        .then(recommendations => {
            Api.cache.setCurrentRecommendationSet(recommendations);
            this.setState({
              isSearching: false,
              searchRecommendations: recommendations
            });
          }
        );
    });

  }

  loadCategoryProducts() {
    Api.cache.setCurrentRecommendationSet(null);
    this.setState({
      activeRecommendationType: RECOMMENDATION_TYPES.CATEGORY,
      isSearching: false,
      categorySearching: true,
      categoryRecommendations: null
    }, () => {
      let request = Api.createProductBrowseNewPayload(this.state.category, this.state.subcategory, this.state.filter);

      let requestUUID = generateUUID();
      let promise = this.loadContinuous(request, requestUUID);
      if (promise) {
        promise.then(() => {
          this.setState({
            categorySearching: false,
            isSearching: false
          })
        });
      }
    });
  }

  loadContinuous(request, requestId) {
    if (this.categoryRequestLock === requestId) return null;

    this.categoryRequestLock = requestId;

    let isEmployee = this.state.user && this.state.user.is_employee;
    let isInvited = this.state.retailer && this.state.retailer.invited;

    if (!isEmployee && !isInvited) return null;

    return Api.productBrowseNew(request)
      .then(response => {
        if (this.categoryRequestLock !== requestId) return;

        let recommendations = response;

        if (response.next_request) {
          let nextRequestId = response.next_request.pagination_token;
          recommendations.loadMore = this.loadContinuous.bind(this, response.next_request, nextRequestId);
        } else {
          this.categoryRequestLock = null;
          recommendations.loadMore = null;
        }

        Api.cache.setCurrentRecommendationSet(recommendations);
        this.setState({
          categoryRecommendations: recommendations
        });
      });
  }


  getMargin(token) {
    let margins = this.state.margins;
    if (Object.keys(margins).indexOf(token) < 0) return 0;
    return margins[token];
  }

  setCategory(category, subcategory, filter) {
    if (this.props.location.pathname.indexOf('category') > 0) {
      this.context.router.push(`/retailer/${this.props.params.retailerToken}`);
    }

    Api.cache.setProductSearchActive(false);
    this.setState({
      category: category || JUST_FOR_YOU_CATEGORY,
      subcategory,
      filter: filter
    });
  }

  categoryBar() {
    return <CategoryBar categories={this.state.categories}
                        filters={CATEGORY_FILTERS}
                        activeCategory={this.state.category}
                        activeSubcategory={this.state.subcategory}
                        activeFilter={this.state.filter}
                        onSelect={this.setCategory}/>
  }

  closeProductModal() {
    this.redirectToRetailerPage();
  }

  getCartSelection() {
    let cartSelection = null;
    let cart = Api.cache.getCart();
    if (cart && cart.cart && cart.cart.items && cart.cart.items.length > 0) {
      let productOptionsInCart = cart.cart.items.filter(cartItem => {
        return cartItem.product_token === this.state.productToken;
      });

      if (productOptionsInCart.length > 0) {
        let product = cart.products[productOptionsInCart[0].product_token];
        let batchCount = product.options[0].unit_multiplier;
        cartSelection = productOptionsInCart.map(cartItem => {
          return {
            option: cartItem.product_option_token,
            batches: cartItem.quantity / batchCount
          };
        });
      }
    }

    return cartSelection;
  }

  productModal() {
    if (this.state.productToken) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }

    let cartSelection = this.getCartSelection();

    return (
      <LightBox onClose={this.closeProductModal} className={this.state.productToken ? '' : 'hidden'}>
        <RetailerProduct
          productToken={this.state.productToken}
          retailerToken={this.props.params.retailerToken}
          onSave={this.addToCart}
          defaultSelection={cartSelection}
          buttonText={!cartSelection ? calculateButtonText : 'Save'}
        />
      </LightBox>
    );
  }

  redirectToRetailerPage() {
    this.context.router.push(`/retailer/${this.props.params.retailerToken}`);
  }

  addToCart(productToken, productOptions) {
    let cartItems = null;

    return this.getRecommendedProduct(productToken)
      .then(product => {
        if (!product) {
          console.error('Cannot find product with token %s', productToken);
          return null;
        }

        cartItems = productOptions.map(selection => {
          let selectedOption = product.options.find((option) => selection.option === option.token);
          let bagEntry = {};
          bagEntry.brand_token = product.brand_token;
          bagEntry.product_token = product.token;
          bagEntry.product_option_token = selectedOption.token;
          bagEntry.quantity = selection.batches * selectedOption.unit_multiplier;
          bagEntry.price_cents = selectedOption.wholesale_price_cents;

          return bagEntry;
        });
      })
      .then(() => {
        let oldSelection = this.getCartSelection();
        // Delete existing items from cart before continuing
        if (oldSelection && oldSelection.length > 0) {
          return Api.removeFromCart(oldSelection.map(selection => selection.option));
        }
      })
      .then(() => Api.addToCart(cartItems))
      .then(result => {
        this.redirectToRetailerPage();
        return result;
      })
      .catch(error => {
        console.error(error);
      });
  }

  render() {
    let retailerInvited = this.state.retailer && this.state.retailer.invited;
    let isEmployee = this.state.user && this.state.user.is_employee;
    let forceLead = this.props.location && this.props.location.query && this.props.location.query.lead === "1";

    if (!this.state.retailer) return null;

    if (forceLead || (!retailerInvited && !isEmployee)) {
      return <NoProducts retailer={this.state.retailer}/>;
    }

    if (this.state.error) {
      return (
        <div className="error">
          {this.state.error.message}
        </div>
      );
    }

    let activeRecommendations = this.state.recommendations;
    if (this.state.isSearching
      || this.state.categorySearching
      || !this.state.categories
      || !activeRecommendations
      || !this.state.retailer) {
      return (
        <div>
          {this.state.categories ? this.categoryBar() : null}
          <div className="retailer-loading-wrapper">
            <Processing />
          </div>
        </div>
      );
    }

    let recommendations = null;
    if (activeRecommendations) {
      let displayOrder = activeRecommendations.display_order;
      let productMap = activeRecommendations.products;

      let isEmployee = this.state.user && this.state.user.is_employee;

      if (isEmployee && this.state.currentFilter.length > 0) {
        displayOrder = displayOrder.filter(productToken => {
          let product = productMap[productToken];
          let enabled = false;

          if (product) {
            if (this.state.currentFilter.length === 1 && this.state.currentFilter[0] === '+ACTIVE') {
              enabled = product.active;
            } else {
              this.state.currentFilter.forEach(filterValue => {
                if (product.recommenders && product.recommenders.indexOf(filterValue) >= 0) enabled = true;
              });
              if (enabled && this.state.currentFilter.indexOf('+ACTIVE') >= 0 && !product.active) enabled = false;
            }
          }

          return enabled;
        });
      }

      if (displayOrder.length === 0) {
        if (this.props.location.query.campaign === "1") return <LeadSubmittedMessage retailer={this.state.retailer}/>;
        let clearFilters = this.state.filter ?
          <div>
            <p>Clear filters above to view all results.</p>
            <p className="clear-filters"
               onClick={() => {
                 this.setCategory(this.state.category, this.state.subcategory, null)
               }}>
              Clear Filters
            </p>
          </div> : null;
        recommendations =
          <div className="no-results">
            <h1>No Items Found</h1>
            <p>Try widening your search to see what’s available. </p>
            {clearFilters}
          </div>;
      } else {
        let loading = activeRecommendations.loadMore ? <Processing /> : null;
        recommendations =
          <div>
            <RecommendationGrid
              products={productMap}
              brands={activeRecommendations.brands}
              collections={activeRecommendations.collections || {}}
              display_order={displayOrder}
              retailerToken={this.props.params.retailerToken}
              selectProduct={this.selectProduct}
              selectCollection={this.selectCollection}
            />
            {loading}
          </div>
      }
    }

    let classNames = ['retailer'];

    let headerTitle = JUST_FOR_YOU_CATEGORY;
    if (this.state.searchTerm) {
      headerTitle = 'Search: ' + this.state.searchTerm;
    } else if (this.state.subcategory) {
      headerTitle = capitalizeEveryWord(this.state.subcategory);
    } else if (this.state.category) {
      headerTitle = capitalizeEveryWord(this.state.category);
    }

    return (
      <div className={classNames.join(' ')}>
        {this.categoryBar()}
        <div className="recommendation-wrapper">
          <BreadCrumb primary={this.state.category}
                      secondary={this.state.subcategory}
                      className={this.state.activeRecommendationType === RECOMMENDATION_TYPES.SEARCH ? "hide" : ""}
                      filter={this.state.category === SHOP_ALL_CATEGORY ? null : this.state.filter}
                      onSelect={(selection) => {
                        if (!selection) {
                          this.setCategory(null, null, this.state.filter);
                        } else if (selection === this.state.category) {
                          this.setCategory(this.state.category, null, this.state.filter);
                        }
                      }}
                      onClearFilters={() => {
                        this.setCategory(this.state.category, this.state.subcategory, null)
                      }}
                      onShopFilters={(filter) => {
                        this.setCategory(SHOP_ALL_CATEGORY, null, filter);
                      }}>
            <div className="recommendations-title">{headerTitle}</div>
          </BreadCrumb>
          {recommendations}
        </div>
        {this.productModal()}
      </div>
    );
  }
}

class LeadSubmittedMessage extends React.Component {
  static propTypes = {
    retailer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);

    this.sendLead = this.sendLead.bind(this);
  }

  componentDidMount() {
    this.sendLead();
  }

  sendLead() {
    let payload = {
      first_name: '',
      last_name: '',
      email_address: 'campaign@indigofair.com',
      phone_number: '',
      note: `Campaign lead for '${this.props.retailer.name}' (${this.props.retailer.token})`
    };

    Api.retailerLead(this.props.retailer.token, payload);
  }

  render() {
    return (
      <div className="no-products">
        <h2>Thank you!</h2>
        <p>Your request has been received. We will email you a link to view the Indigo Fair product catalogue within
          24 hours</p>
      </div>
    )
  }
}



/** WEBPACK FOOTER **
 ** ./src/views/Retailer.js
 **/